import { AnimatePresence } from "framer-motion";
import SidebarLinks from "../components/Sidebar/SidebarLinks";
import { useSidebar } from "../contexts/SidebarContext";
import { motion } from "framer-motion";
import Logo from "@/components/Sidebar/Logo";
import QrCode from "@/components/QrCode";
import { FiHelpCircle } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useUserRole } from "@/hooks/useUserRole";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import ProfileImage from "@/components/ProfileImage";

function Sidebar() {
  const { isSidebarOpen, isShowingIconsOnly } = useSidebar();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const xOffset = locale === "ar" ? 260 : -260;

  const userData = useSelector((state: RootState) => state.user.userData);
  const userName =  userData?.username || "User";
  const userAvatar = userData?.avatar || null

  const { RoleNamesEnAr, isAdmin, isBranchAdmin } = useUserRole();


  return (
    <AnimatePresence>
      <motion.aside
        initial={{ x: 0 }}
        animate={{ x: isSidebarOpen ? 0 : xOffset }}
        transition={{ duration: 0.1, type: "tween" }}
        className="z-[1000] col-start-1 col-end-1 row-start-2 row-end-13 flex h-full translate-x-[260px] flex-col justify-between overflow-y-auto overflow-x-visible bg-sidebar px-4 pb-5 pt-3 text-white duration-200 md:flex md:translate-x-0"
      >
        {/* Logo with animation */}
        <AnimatePresence mode="wait">
          {isShowingIconsOnly ? (
            <motion.div
              key="icons-only-logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.1, duration: 0.2 }}
              className="hidden"
            >
              <Logo />
            </motion.div>
          ) : (
            <motion.div
              key="full-logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              // exit={{ opacity: 0 }}
              // transition={{ delay: 0.1, duration: 0.2 }}
            >
              <Logo />
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Code with animation */}
        {(isAdmin || isBranchAdmin) &&  (
          <AnimatePresence mode="wait">
            {isShowingIconsOnly ? (
              <motion.div
                key="icons-only-qr"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.1, duration: 0.2 }}
                className="hidden"
              >
                <QrCode />
              </motion.div>
            ) : (
              <motion.div
                key="full-qr"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                // exit={{ opacity: 0 }}
                // transition={{ delay: 0.1, duration: 0.2 }}
              >
                <QrCode />
              </motion.div>
            )}
          </AnimatePresence>
        )}

        <div className="flex flex-grow flex-col duration-700">
          <SidebarLinks />
        </div>

        {/* Name & Role */}
        {!isShowingIconsOnly && (
          <div className="mt-6 flex select-none items-center justify-start gap-2 rounded-md bg-skyblue p-2 text-start text-sm font-semibold text-sidebar md:hidden">
            <ProfileImage imageUrl={userAvatar} size={40} iconClassName="!text-sidebar" />
            <div className="flex-col items-center justify-center">
              <p className="w-full">{userName}</p>
              <p className="w-full">
                {locale === "en" ? RoleNamesEnAr.en : RoleNamesEnAr.ar}
              </p>
            </div>
          </div>
        )}

        {/* Help Center */}
        {(isAdmin || isBranchAdmin) && (
          <div className="mt-auto pt-10">
            <a
              href="https://interflow.sa/%d9%85%d8%b1%d9%83%d8%b2-%d8%a7%d9%84%d9%85%d8%b3%d8%a7%d8%b9%d8%af%d8%a9/"
              target="_blank"
              title={t("sidebar.helpCenter") as string}
              className={`flex w-full cursor-pointer items-center ${isShowingIconsOnly ? "justify-start ps-3 text-xl" : "justify-center"} gap-1 text-center text-skyblue underline`}
            >
              {isShowingIconsOnly ? null : (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {t("sidebar.helpCenter")}
                </motion.span>
              )}
              <span>
                <FiHelpCircle />
              </span>
            </a>
          </div>
        )}
      </motion.aside>
    </AnimatePresence>
  );
}

export default Sidebar;
